'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import StatusBadge, { CaseStatus } from './StatusBadge';
import AppTypeBadge, { AppType } from './AppTypeBadge';

export interface CaseRecord {
  id: string;
  caseRef: string;
  clientName: string;
  clientEmail: string;
  appType: AppType;
  submissionDate: string;
  lastActivity: string;
  lastActivityLabel: string;
  status: CaseStatus;
  hasNewActivity: boolean;
  documentsCount: number;
}

interface SortConfig {
  field: string;
  dir: 'asc' | 'desc';
}

interface CaseTableProps {
  cases: CaseRecord[];
  sortConfig: SortConfig;
  onSortChange: (field: string) => void;
}

const SortIcon = ({ field, sortConfig }: { field: string; sortConfig: SortConfig }) => {
  if (sortConfig.field !== field) {
    return <Icon name="ChevronUpDownIcon" size={14} variant="outline" className="text-text-secondary opacity-50" />;
  }
  return sortConfig.dir === 'asc'
    ? <Icon name="ChevronUpIcon" size={14} variant="outline" className="text-primary" />
    : <Icon name="ChevronDownIcon" size={14} variant="outline" className="text-primary" />;
};

const CaseTable = ({ cases, sortConfig, onSortChange }: CaseTableProps) => {
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);

  const thClass = "px-4 py-3 text-left text-xs font-caption font-500 text-text-secondary uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-text-primary select-none";

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className={thClass} onClick={() => onSortChange('caseRef')}>
                <div className="flex items-center gap-1.5">Case Ref <SortIcon field="caseRef" sortConfig={sortConfig} /></div>
              </th>
              <th className={thClass} onClick={() => onSortChange('clientName')}>
                <div className="flex items-center gap-1.5">Client Name <SortIcon field="clientName" sortConfig={sortConfig} /></div>
              </th>
              <th className={thClass} onClick={() => onSortChange('appType')}>
                <div className="flex items-center gap-1.5">Type <SortIcon field="appType" sortConfig={sortConfig} /></div>
              </th>
              <th className={thClass} onClick={() => onSortChange('submissionDate')}>
                <div className="flex items-center gap-1.5">Submitted <SortIcon field="submissionDate" sortConfig={sortConfig} /></div>
              </th>
              <th className={thClass} onClick={() => onSortChange('status')}>
                <div className="flex items-center gap-1.5">Status <SortIcon field="status" sortConfig={sortConfig} /></div>
              </th>
              <th className={thClass} onClick={() => onSortChange('lastActivity')}>
                <div className="flex items-center gap-1.5">Last Activity <SortIcon field="lastActivity" sortConfig={sortConfig} /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-caption font-500 text-text-secondary uppercase tracking-wider">Docs</th>
              <th className="px-4 py-3 text-right text-xs font-caption font-500 text-text-secondary uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {cases.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Icon name="DocumentTextIcon" size={40} variant="outline" className="text-text-secondary opacity-40" />
                    <p className="text-text-secondary text-sm">No cases found matching your filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              cases.map((c) => (
                <tr key={c.id} className="hover:bg-muted/50 transition-standard group">
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      {c.hasNewActivity && (
                        <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" title="New activity" />
                      )}
                      <span className="font-data text-sm text-primary font-medium">{c.caseRef}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-text-primary">{c.clientName}</p>
                      <p className="text-xs font-caption text-text-secondary">{c.clientEmail}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <AppTypeBadge type={c.appType} />
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm font-caption text-text-secondary">{c.submissionDate}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-xs font-caption text-text-secondary">{c.lastActivityLabel}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1 text-xs font-caption text-text-secondary">
                      <Icon name="PaperClipIcon" size={13} variant="outline" />
                      <span>{c.documentsCount}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        href="/case-detail-management"
                        className="flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-primary hover:bg-primary/10 transition-standard"
                        title="View case details"
                      >
                        <Icon name="EyeIcon" size={16} variant="outline" />
                      </Link>
                      <Link
                        href="/case-detail-management"
                        className="flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-secondary hover:bg-secondary/10 transition-standard"
                        title="Manage documents"
                      >
                        <Icon name="FolderOpenIcon" size={16} variant="outline" />
                      </Link>
                      <Link
                        href="/case-detail-management"
                        className="flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-accent hover:bg-accent/10 transition-standard"
                        title="View activity timeline"
                      >
                        <Icon name="ClockIcon" size={16} variant="outline" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="lg:hidden divide-y divide-border">
        {cases.length === 0 ? (
          <div className="py-16 text-center">
            <Icon name="DocumentTextIcon" size={40} variant="outline" className="text-text-secondary opacity-40 mx-auto" />
            <p className="text-text-secondary text-sm mt-3">No cases found.</p>
          </div>
        ) : (
          cases.map((c) => (
            <div key={c.id} className="p-4">
              <div
                className="flex items-start justify-between gap-3 cursor-pointer"
                onClick={() => setExpandedMobile(expandedMobile === c.id ? null : c.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {c.hasNewActivity && <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />}
                    <span className="font-data text-sm text-primary font-medium">{c.caseRef}</span>
                    <AppTypeBadge type={c.appType} />
                  </div>
                  <p className="text-sm font-medium text-text-primary truncate">{c.clientName}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <StatusBadge status={c.status} />
                  </div>
                </div>
                <Icon
                  name={expandedMobile === c.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={18}
                  variant="outline"
                  className="text-text-secondary flex-shrink-0 mt-1"
                />
              </div>

              {expandedMobile === c.id && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-caption text-text-secondary">Email</span>
                    <span className="text-text-primary">{c.clientEmail}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-caption text-text-secondary">Submitted</span>
                    <span className="text-text-primary">{c.submissionDate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-caption text-text-secondary">Last Activity</span>
                    <span className="text-text-primary">{c.lastActivityLabel}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-caption text-text-secondary">Documents</span>
                    <span className="text-text-primary">{c.documentsCount}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Link href="/case-detail-management" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90 transition-standard">
                      <Icon name="EyeIcon" size={15} variant="outline" />
                      <span>View</span>
                    </Link>
                    <Link href="/case-detail-management" className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-border text-text-primary text-sm rounded-md hover:bg-muted transition-standard">
                      <Icon name="FolderOpenIcon" size={15} variant="outline" />
                      <span>Docs</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default CaseTable;